


import {Component, Emit, Prop, Vue} from 'vue-property-decorator';
import {DomElementStyle} from '@/library/dom-element/dom-element-style';
import {MediaQueryService} from '@/services/media-query.service';
import AppScrollBox from '@/components/scroll-box/scroll-box.vue';
import {Subject} from 'rxjs';
import {take, timeout} from 'rxjs/operators';
import {CustomObservable} from '@/library/observable/custom-observable';

@Component({})
export default class AppMapObjectContainer extends Vue {

  @Prop({default: false})
  public disabled!: boolean;

  @Prop({default: false})
  public alwaysExpanded!: boolean;

  @Emit('close')
  public emitClose(): void {
  }

  public closed: boolean = true;
  public swiping: boolean = false;
  private localExpanded: boolean = false;
  private startTouch: Touch | null = null;
  private lastTouch: Touch | null = null;
  private containerRef: HTMLElement | null = null;
  private scrollBoxRef: AppScrollBox | null = null;
  private mediaQueryService: MediaQueryService = MediaQueryService.getInstance();
  private whenSwipeStarted: Subject<void> = new Subject();

  public mounted(): void {
    this.containerRef = this.$refs.container as HTMLElement;
    this.scrollBoxRef = this.$refs.scrollBox as AppScrollBox;
    if (this.alwaysExpanded || this.mediaQueryService.aboveMd) {
      this.expand();
    }
    this.closed = false;
  }

  public expanded(): boolean {
    return this.localExpanded;
  }

  public expand(): void {
    if (!this.disabled && !!this.containerRef) {
      this.localExpanded = true;
      this.containerRef.style.height = '';
      this.containerRef.style.maxHeight = '';
    }
  }

  public collapse(): void {
    if (!this.disabled && !!this.containerRef) {
      this.localExpanded = false;
      this.containerRef.style.height = '';
      this.containerRef.style.maxHeight = '';
    }
  }

  public close(): void {
    this.closed = true;
    setTimeout(() => this.emitClose(), 200);
  }

  public startSwipe(event: TouchEvent | MouseEvent): void {
    setTimeout(() => {
      if (this.canStartSwipe(event)) {
        event = event as TouchEvent;
        this.swiping = true;
        this.startTouch = event.touches[0];
        this.lastTouch = event.touches[0];
        this.whenSwipeStarted.next();
      }
    }, 100);
  }

  public swipe(event: TouchEvent | MouseEvent): void {
    if (this.swiping && !!this.containerRef) {
      event = event as TouchEvent;
      const firstTouch: Touch = event.touches[0];
      const style = new DomElementStyle(this.containerRef);
      const distance = this.lastTouch ? this.lastTouch.clientY - firstTouch.clientY : 0;
      const height = style.height() + distance;
      this.containerRef.style.height = height + 'px';
      this.containerRef.style.maxHeight = height + 'px';
      this.lastTouch = firstTouch;
    }
  }

  public endSwipe(event: TouchEvent | MouseEvent): void {
    const whenSwipeStarted = this.swiping ? CustomObservable.empty() : this.whenSwipeStarted;
    whenSwipeStarted.pipe(timeout(100), take(1)).subscribe({
      next: () => {
        if (this.canEndSwipe(event)) {
          event = event as TouchEvent;
          const firstTouch: Touch = event.changedTouches[0];
          const distance = this.startTouch ? this.startTouch.clientY - firstTouch.clientY : 0;
          const threshold: number = 80;
          this.startTouch = null;
          this.lastTouch = null;
          this.swiping = false;
          if (this.expanded()) {
            distance < -threshold ? this.close() : this.expand();
          } else {
            if (distance > threshold) {
              this.expand()
            } else if (distance < -threshold) {
              this.close();
            } else {
              this.collapse()
            }
          }
        }
      },
      error: () => {},
    });
  }

  private canStartSwipe(event: TouchEvent | MouseEvent): boolean {
    return this.canSwipe(event)
        && this.isElementAllowedToSwipe(event.target as HTMLElement)
        && !!this.scrollBoxRef
        && !this.scrollBoxRef.scrolling();
  }

  private canEndSwipe(event: TouchEvent | MouseEvent): boolean {
    return this.canSwipe(event) && !!this.startTouch;
  }

  private canSwipe(event: TouchEvent | MouseEvent): boolean {
    return (event as any).touches
        && !this.disabled
        && !!this.scrollBoxRef
        && (!this.expanded() || this.scrollBoxRef.scrolledTop());
  }

  private isElementAllowedToSwipe(element: HTMLElement): boolean {
    return !['button', 'a'].includes(element.tagName.toLocaleLowerCase());
  }
}
